import { event } from 'jquery';
import { wrapFetch } from '../libs/fetch';
import type {
  Applicant,
  FetchApplicantIdsResponse,
  FetchApplicantsResponse,
  SearchParams,
} from '../types/eventApplicantListPage';

export const getApplicants = async (eventContentId: number) => {
  return await wrapFetch<FetchApplicantsResponse>(
    `/api/v1/entry_event_contents/${eventContentId}`
  );
};

export const searchApplicants = async (params: SearchParams) => {
  const queryParams = new URLSearchParams();

  if (params.eventIds) {
    queryParams.append('eventIds', params.eventIds);
  }
  if (params.eventContentId) {
    queryParams.append('eventContentId', params.eventContentId);
  }
  if (params.applicantName) {
    queryParams.append('applicantName', params.applicantName);
  }

  const url = `/api/v1/entry_event_contents?${queryParams.toString()}`;

  const response = await wrapFetch<FetchApplicantIdsResponse>(url);

  return response;
};

export const fetchApplicantsCount = async (params: SearchParams, signal?: AbortSignal) => {
  const queryParams = new URLSearchParams();

  if (params.eventIds) {
    queryParams.append('eventIds', params.eventIds);
  }
  if (params.eventContentId) {
    queryParams.append('eventContentId', params.eventContentId);
  }
  if (params.applicantName) {
    queryParams.append('applicantName', params.applicantName);
  }

  const url = `/api/v1/entry_event_contents/count?${queryParams.toString()}`;

  return await wrapFetch<{ totalCount: number }>(url, { signal });
};

export const fetchApplicant = async (id: number) => {
  return await wrapFetch<Applicant>(
    `/api/v1/entry_event_contents/${id}/show_applicant`
  );
};

export const cancelBulkEventApplicants = async (entryEventContentIds: number[], csrfToken: string) => {
  const response = await fetch('/api/v1/entry_event_contents/bulk_cancel', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({ entry_event_content_ids: entryEventContentIds }),
    credentials: 'same-origin',
  });
  const data = await response.json() as { success?: boolean; cancelledPersonCount?: number; error?: string };

  if (!response.ok || data.cancelledPersonCount == null) {
    throw new Error(data.error || 'キャンセル処理に失敗しました。');
  }

  return { success: true, cancelledPersonCount: data.cancelledPersonCount };
};

export class EntryEventContentApiError extends Error {
  status: number;
  recipientCount?: number;

  constructor(message: string, status: number, recipientCount?: number) {
    super(message);
    this.status = status;
    this.recipientCount = recipientCount;
  }
}

const fetchEntryEventContentApi = async <T>(url: string, body: object, csrfToken: string): Promise<T> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });
  const data = await response.json() as T & { error?: string; recipientCount?: number };

  if (!response.ok) {
    throw new EntryEventContentApiError(
      data.error || '処理に失敗しました。',
      response.status,
      data.recipientCount
    );
  }

  return data;
};

export const fetchBulkMessageRecipientCount = async (entryEventContentIds: number[], csrfToken: string) => {
  return await fetchEntryEventContentApi<{ recipientCount: number }>(
    '/api/v1/entry_event_contents/bulk_message_recipient_count',
    { entry_event_content_ids: entryEventContentIds },
    csrfToken
  );
};

// 一覧に表示中の応募id配列を重複排除した「応募人数」を取得する（「◯件」と対になる「◯人」）。
export const fetchEventApplicantPersonCount = async (entryEventContentIds: number[], csrfToken: string) => {
  return await fetchEntryEventContentApi<{ personCount: number }>(
    '/api/v1/entry_event_contents/person_count',
    { ids: entryEventContentIds },
    csrfToken
  );
};

export const postBulkEventMessages = async (
  entryEventContentIds: number[],
  expectedRecipientCount: number,
  messageBody: string,
  csrfToken: string
) => {
  return await fetchEntryEventContentApi<{ success: boolean }>(
    '/api/v1/entry_event_contents/bulk_messages',
    {
      entry_event_content_ids: entryEventContentIds,
      expected_recipient_count: expectedRecipientCount,
      body: messageBody,
    },
    csrfToken
  );
};

export const postBulkMessages = async (applicantIds: number[], messageBody: string, csrfToken: string) => {
  return await wrapFetch<{ success: boolean }>(
    '/api/v1/entry_event_contents/bulk_messages',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        applicant_ids: applicantIds,
        body: messageBody,
      }),
      credentials: 'same-origin'
    }
  );
};

export const downloadCsv = (eventContentId: number): void => {
  window.location.href = `/entry_event_contents/${eventContentId}/csv_download`;
};

type DownloadCsvParams = {
  eventIds?: string;
  eventContentId?: string;
  applicantName?: string;
};

export const downloadEventApplicantsCsv = async (
  params: DownloadCsvParams,
  csrfToken: string
): Promise<{ data: string }> => {
  return await wrapFetch<{ data: string }>('/api/v1/entry_event_contents/download_csv', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({
      eventIds: params.eventIds,
      eventContentId: params.eventContentId,
      applicantName: params.applicantName,
    }),
    credentials: 'same-origin',
  });
};

export const applyEventContent = async (eventContentId: number, csrfToken: string): Promise<void> => {
  return await wrapFetch<void>('/api/v1/entry_event_contents', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({
      event_content_id: eventContentId
    })
  });
};

export const participateEventContent = async (eventContentId: number, csrfToken: string): Promise<void> => {
  return await wrapFetch<void>(`/api/v1/entry_event_contents/${eventContentId}/participate`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({
      event_content_id: eventContentId
    })
  });
};

export const cancelEventContent = async (eventContentId: number, csrfToken: string): Promise<void> => {
  const response = await fetch(`/api/v1/entry_event_contents/${eventContentId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    }
  });
  if (!response.ok) {
    throw new Error(`Failed to cancel event content: ${response.status}`);
  }
  return;
};
